import {Dialog, IconButton} from '@wandb/components';
import React, {type FC} from 'react';

import {Tailwind} from '../Tailwind';
import {
  SecretFormFields,
  SecretFormFooter,
  type SecretFormMode,
  useSecretFormState,
} from './SecretFormContent';

type SecretDrawerProps = {
  isOpen: boolean;
  mode: SecretFormMode;
  secretName?: string;
  existingSecretNames: string[];
  isSaving: boolean;
  /** Distance from the top of the viewport, e.g. to sit below a navbar. */
  offsetTop?: number;
  onClose: () => void;
  onSave: (input: {secretName: string; secretValue: string}) => Promise<void>;
  banner?: React.ReactNode;
  renderNameWarning?: (secretName: string) => React.ReactNode;
  saveLabel?: string;
};

type SecretDrawerContentProps = Omit<SecretDrawerProps, 'secretName'> & {
  initialSecretName: string;
};

const SecretDrawerContent: FC<SecretDrawerContentProps> = ({
  isOpen,
  mode,
  initialSecretName,
  existingSecretNames,
  isSaving,
  offsetTop,
  onClose,
  onSave,
  banner,
  renderNameWarning,
  saveLabel,
}) => {
  const state = useSecretFormState({
    mode,
    initialSecretName,
    existingSecretNames,
    isSaving,
    onSave,
  });

  const title = mode === 'edit' ? 'Edit secret' : 'Create new secret';

  return (
    <Dialog.Drawer
      isOpen={isOpen}
      offsetTop={offsetTop}
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}>
      <Tailwind>
        <div className="flex min-h-[56px] items-center justify-between border-b border-moon-250 px-20 py-16 text-xl font-semibold text-moon-800">
          <Dialog.Heading mb={0}>{title}</Dialog.Heading>
          <IconButton
            icon="close"
            aria-label="Close drawer"
            tooltip={{children: 'Close drawer'}}
            variant="ghost"
            onPress={onClose}
          />
        </div>
      </Tailwind>

      <Dialog.Content>
        <SecretFormFields
          mode={mode}
          state={state}
          banner={banner}
          renderNameWarning={renderNameWarning}
        />
      </Dialog.Content>

      <Tailwind>
        <Dialog.Footer>
          <SecretFormFooter
            mode={mode}
            state={state}
            saveLabel={saveLabel}
            onCancel={onClose}
          />
        </Dialog.Footer>
      </Tailwind>
    </Dialog.Drawer>
  );
};

export const SecretDrawer: FC<SecretDrawerProps> = ({
  mode,
  secretName,
  ...props
}) => {
  const initialSecretName = mode === 'edit' ? (secretName ?? '') : '';
  // Remount the drawer content so form state resets whenever the drawer is
  // reopened or retargeted at a different secret.
  const drawerScopeKey = `${mode}:${initialSecretName}:${
    props.isOpen ? 'open' : 'closed'
  }`;

  return (
    <SecretDrawerContent
      key={drawerScopeKey}
      mode={mode}
      initialSecretName={initialSecretName}
      {...props}
    />
  );
};
